import { sortBy } from "lodash";
import { DimensionCutoff } from "../../../psyEvaluations.types";
import { RowDimension, ScoresColumn } from "../scoresTable.types";
import { getMatchedColor } from "../scoresTableUtils";

export const groupedNormsLegendColumn: ScoresColumn = {
	id: "groupedNorms",
	isVisible: (context) => groupBySharedNorms(context.rowsDimensions).some((group) => group.bands.length > 0),
	getHeader: () => ({ label: "Legenda" }),
	getCell: (_row, rowIndex, context) => {
		const group = groupBySharedNorms(context.rowsDimensions).find((candidate) => candidate.startIndex === rowIndex);
		if (!group) return null;
		const entries = group.bands.map((band) => ({
			key: band.key,
			swatchColor: getMatchedColor(band.cutoff, context.levelColors),
			rangeText: band.range,
			description: band.description,
		}));
		return { rowSpan: group.rowSpan, content: { type: "legend", entries, entryAlign: "flex-start" } };
	},
};

type NormBand = {
	key: string;
	range: string;
	description: string;
	cutoff: DimensionCutoff;
};

type NormGroup = {
	startIndex: number;
	rowSpan: number;
	signature: string;
	bands: Array<NormBand>;
};

const formatBound = (value: number | string) => {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return `${value}`;
	return `${Math.round(numericValue * 100) / 100}`;
};

const formatCutoffRange = (cutoff: DimensionCutoff, index: number, sortedCutoffs: Array<DimensionCutoff>) => {
	const isFirstBand = index === 0;
	const isLastBand = index === sortedCutoffs.length - 1;
	if (sortedCutoffs.length === 2) {
		const boundary = formatBound(sortedCutoffs[1].value_min);
		return isFirstBand ? `<${boundary}` : `≥${boundary}`;
	}
	if (isFirstBand && sortedCutoffs.length > 1) return `<${formatBound(sortedCutoffs[index + 1].value_min)}`;
	if (isLastBand && sortedCutoffs.length > 1) return `>${formatBound(sortedCutoffs[index - 1].value_max)}`;
	return `${formatBound(cutoff.value_min)}-${formatBound(cutoff.value_max)}`;
};

const buildBands = (row: RowDimension) => {
	const cutoffsJunction = row.instrument_dimension?.dimension_cut_offs ?? [];
	const cutoffs = cutoffsJunction.map((junctionRow) => junctionRow.instrument_dimension_cutoffs_id).filter(Boolean);
	const sortedCutoffs = sortBy(cutoffs, (cutoff) => Number(cutoff.value_min));
	const bands = sortedCutoffs.map((cutoff, index) => {
		const band: NormBand = {
			key: `${cutoff.value_min}_${cutoff.value_max}`,
			range: formatCutoffRange(cutoff, index, sortedCutoffs),
			description: cutoff.description ?? "",
			cutoff,
		};
		return band;
	});
	return bands;
};

const buildSignature = (bands: Array<NormBand>) => {
	const signature = bands
		.map((band) => `${band.cutoff.value_min}|${band.cutoff.value_max}|${band.cutoff.level ?? ""}|${band.cutoff.description ?? ""}`)
		.join("||");
	return signature;
};

const groupBySharedNorms = (rowsDimensions: Array<RowDimension>) => {
	const groups: Array<NormGroup> = [];
	rowsDimensions.forEach((row, index) => {
		const bands = buildBands(row);
		const signature = buildSignature(bands);
		const lastGroup = groups[groups.length - 1];
		if (lastGroup && lastGroup.signature === signature) {
			lastGroup.rowSpan += 1;
			return;
		}
		groups.push({ startIndex: index, rowSpan: 1, signature, bands });
	});
	return groups;
};
